import { i18n } from '@/plugins/i18n';


/**
 * Information from URL.
 * Contains a regex to match the target URL against.
 * Contains an 'infoTr' key that holds the localization for the URL's usage.
 * @type {Array}
 */
class XhrMatcher {
  static XHR_CREATE_PROBLEM = new XhrMatcher('post', /\/server\/problems/, 'CREATE', 'PROBLEM');

  static XHR_UPDATE_PROBLEM = new XhrMatcher('put', /\/server\/problems\/-?\d+/, 'UPDATE', 'PROBLEM');

  static XHR_DELETE_PROBLEM = new XhrMatcher('delete', /\/server\/problems\/(-?\d+)/, 'DELETE', 'PROBLEM');

  static XHR_CREATE_CAUSE = new XhrMatcher('post', /\/legacy\/problems\/addCause\.php/, 'CREATE', 'CAUSE');

  static XHR_UPDATE_CAUSE = new XhrMatcher('post', /\/legacy\/problems\/editCause\.php/, 'UPDATE', 'CAUSE');

  static XHR_DELETE_CAUSE = new XhrMatcher('post', /\/legacy\/problems\/deleteCause\.php/, 'DELETE', 'CAUSE');

  static XHR_CREATE_ACTION = new XhrMatcher('post', /\/legacy\/problems\/addAction\.php/, 'CREATE', 'ACTION');

  static XHR_UPDATE_ACTION = new XhrMatcher('post', /\/legacy\/problems\/editAction\.php/, 'UPDATE', 'ACTION');

  static XHR_DELETE_ACTION = new XhrMatcher('post', /\/legacy\/actions\/delete\.php/, 'DELETE', 'ACTION');

  static XHR_UPDATE_INDIC = new XhrMatcher('post', /\/legacy\/indicators\/setValue\.php/, 'UPDATE', 'INDICATOR');

  static XHR_DELETE_INDIC = new XhrMatcher('post', /\/legacy\/indicators\/deleteValue\.php/, 'DELETE', 'INDICATOR');

  method = 'post';

  urlRegex = '';

  type = 'CREATE';

  dataType = 'PROBLEM';

  constructor(method, urlRegex, type, dataType) {
    this.method = method;
    this.urlRegex = urlRegex;
    this.type = type;
    this.dataType = dataType;
  }

  matchesConfig(config) {
    return this.urlRegex.test(config.url) && config.method === this.method;
  }

  isConfigSyncable(config) {
    if (!this.matchesConfig(config)) {
      return false;
    }

    switch (this.codeName) {
      case 'UPDATE_PROBLEM': {
        // Offline problems are synced from the state's content, no need to sync every update
        return config.data && config.data.id > 0;
      }
      case 'DELETE_PROBLEM': {
        // If the problem was created offline, it won't be synced, so no need to delete it
        const match = config.url.match(this.urlRegex);
        return match && match[1] && Number(match[1]) > 0;
      }
      case 'CREATE_CAUSE': {
        // Causes created on offline problems will be synced using the state's content
        return config.data && config.data.id > 0 && !(config.data.cause < 0);
      }
      case 'UPDATE_CAUSE': {
        // Offline causes are synced from the state's content, no need to sync every update
        return config.data && config.data.id > 0;
      }
      case 'DELETE_CAUSE': {
        // If the cause was created offline, it won't be synced, so no need to delete it
        return config.data && config.data.id > 0;
      }
      case 'UPDATE_ACTION': {
        // Offline actions are synced from the state's content, no need to sync every update
        return config.data && config.data.id > 0;
      }
      case 'DELETE_ACTION': {
        // If the action was created offline, it won't be synced, so no need to delete it
        return config.data && config.data.id > 0;
      }
      default:
        return true;
    }
  }

  get codeName() {
    return `${this.type}_${this.dataType}`;
  }

  get info() {
    const type = i18n.t(`message.NETWORK_MGR_${this.type}`);
    const dataType = i18n.t(`message.${this.dataType}`);
    return i18n.t('message.NETWORK_MGR_NAME', { type, dataType });
  }
}

export const allXhrMatchers = [
  XhrMatcher.XHR_CREATE_PROBLEM,
  XhrMatcher.XHR_UPDATE_PROBLEM,
  XhrMatcher.XHR_DELETE_PROBLEM,
  XhrMatcher.XHR_CREATE_CAUSE,
  XhrMatcher.XHR_UPDATE_CAUSE,
  XhrMatcher.XHR_DELETE_CAUSE,
  XhrMatcher.XHR_CREATE_ACTION,
  XhrMatcher.XHR_UPDATE_ACTION,
  XhrMatcher.XHR_DELETE_ACTION,
  XhrMatcher.XHR_UPDATE_INDIC,
  XhrMatcher.XHR_DELETE_INDIC,
];

const allUrlLength = allXhrMatchers.length;

/**
 * Find the information object about a URL, based on allXhrMatchers.
 * @param  {String} url  The URL to find info about
 * @return {Object}  Info about the URL or null if none found.
 */
export function makeSyncableConfig(config) {
  for (let i = 0; i < allUrlLength; i++) {
    const matcher = allXhrMatchers[i];
    if (matcher.isConfigSyncable(config)) {
      config.matcher = matcher;
      return config;
    }
  }
  return config;
}

export default {
  makeSyncableConfig,
};
